import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  ApiService,
  AppLoaderService,
  AuthService,
  BackgroundService,
  CookieConsentService,
  DomainReplacerService,
  FontsService,
  MessageBoxService,
  ModalsContainerService,
  ThirdPartyScriptsService,
  UserService,
} from '@core/services';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { HEROES } from '@shared/data/heroes';
import { Subscription } from 'rxjs';
import { filter, first, map, skip, switchMap } from 'rxjs/operators';
import { updatePlace } from './modals/modal-place';
import { MenarService } from '@games/menar/menar.service';
import { SpinnerOverlayService } from './new-core/archived/services/spinner-overlay.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FreeStudentSubscriptionsForTrialUserService } from '@core/services/promotions/free-student-subscriptions-for-trial-user.service';
import { PromoTimersDialogService } from '@core/services/promotions/promo-timers-dialog.service';
import { FocusLoggerService } from '@core/dev-tools/services/focus-logger.service';
import { CalculatorContainer } from '@games/core/focus/CalculatorContainer';
import { WinterModeService } from '@shared/components/snow-animation/services/winter-mode.service';
import { DocDialogService, DocDialogType } from '@core/services/doc-dialog.service';
import { TranslationFallbackService } from '@core/i18n';
import {
  resolveTranslationFailureMessage,
  TRANSLATION_FAILURE_MESSAGES,
} from '@core/i18n/translation-failure-messages';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  providers: [CalculatorContainer],
})
export class AppComponent {
  // Маркер сборки для проверки атомарного деплоя (смена хеша main-*.js)
  readonly buildMarker = 'deploy-atomic-v1';

  @ViewChild('content', { static: true }) content;

  @ViewChild('dialogs', { static: true, read: ViewContainerRef })
  dialogs: ViewContainerRef;

  isFullscreen: boolean;

  showPanels: boolean;

  diploma: boolean = false;

  showPromptButton: boolean = false;

  progressBarShow = false;

  showCookieConsentBanner = false;

  get isSpinnerShown(): boolean {
    return this.spinnerOverlayService.isSpinnerShown;
  }

  get showWarning() {
    return !!window.localStorage.getItem('showWarning');
  }

  set showWarning(value: boolean) {
    window.localStorage.setItem('showWarning', value ? '1' : '');
  }

  private loadedUser;

  private _notificationAboutSyllablesAddedSubscription?: Subscription;
  private _cookieConsentSubscription?: Subscription;

  /**
   * Флаг, что постоянное уведомление о финальном провале загрузки переводов уже показано.
   * Нужен, чтобы повторный детект не плодил дубликаты тоста.
   */
  private _translationFailureNotificationShown = false;

  private readonly _focusLogger = inject(FocusLoggerService);

  get showSnow(): boolean {
    return this._winterModeService.statusWinterMode();
  }

  constructor(
    @Inject(DOCUMENT) private document: any,
    private translate: TranslateService,
    public backgroundService: BackgroundService,
    private _router: Router,
    private authService: AuthService,
    private _modalsContainerService: ModalsContainerService,
    private _fontsService: FontsService,
    private _cdr: ChangeDetectorRef,
    private userService: UserService,
    private menarService: MenarService,
    private apiService: ApiService,
    private spinnerOverlayService: SpinnerOverlayService,
    private _matIconRegistry: MatIconRegistry,
    private _sanitizer: DomSanitizer,
    private _messageBoxService: MessageBoxService,
    private _freeStudentSubscriptionsPromotionService: FreeStudentSubscriptionsForTrialUserService,
    private _promoTimersDialogService: PromoTimersDialogService,
    private readonly _winterModeService: WinterModeService,
    private domainReplacer: DomainReplacerService,
    private readonly _docDialogService: DocDialogService,
    private readonly _cookieConsentService: CookieConsentService,
    private readonly _thirdPartyScriptsService: ThirdPartyScriptsService,
    private readonly _translationFallbackService: TranslationFallbackService,
    private readonly _appLoaderService: AppLoaderService,
    private readonly _toastrService: ToastrService,
  ) {
    // this._focusLogger.startLogging();

    // this.authService.checkAuth();
    this._router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.progressBarShow = true;
      this._cdr.markForCheck();
    });

    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.progressBarShow = false;
      this._cdr.markForCheck();
      this._appLoaderService.markFirstNavigationReady();
    });

    this._router.events.pipe(filter((event) => event instanceof NavigationCancel)).subscribe(() => {
      this.progressBarShow = false;
      this._cdr.markForCheck();
      this._appLoaderService.markFirstNavigationReady();
    });

    // Перезагрузка страницы при ошибке загрузки lazy-чанка после деплоя
    this._router.events
      .pipe(filter((event) => event instanceof NavigationError))
      .subscribe((event) => {
        if (event instanceof NavigationError) {
          this._appLoaderService.markFirstNavigationReady();

          const isChunkError =
            event.error?.message?.includes('Failed to fetch dynamically imported module') ||
            event.error?.name === 'ChunkLoadError';
          if (isChunkError) {
            window.location.href = event.url;
          }
        }
      });

    this.getFontsCSS();
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const urlFragments = event.url.split('/');
        if (urlFragments.length > 2 && urlFragments[1] === 'games') this.showPanels = false;
        else if (urlFragments.length > 2 && urlFragments[1] === 'exercises')
          this.showPanels = false;
        else if (urlFragments[1].indexOf('diploma') === 0) this.diploma = true;
        else {
          this.showPanels = true;
          // this.router.navigateByUrl('/diploma');
        }
      }
    });

    backgroundService.randomBackground();

    // Настройки Menar будут загружены автоматически через BackgroundService
    // Подписываемся на обновления настроек, чтобы синхронизировать их в MenarService
    backgroundService.settingsUser$.subscribe(() => {
      this.menarService.loadSettings();
    });

    this._setupMatIconRegistry();
    this._uninstallServiceWorkerAndClearCache();
    this._setupDomainReplacer();
  }

  getFontsCSS() {
    this._fontsService.getFonts().subscribe({
      next: (r) => {
        const newStyle = document.createElement('style');
        r.map((s) => {
          newStyle.appendChild(
            document.createTextNode(
              '\
        @font-face {\
          font-family: ' +
                s.family +
                ";\
          src: url('" +
                s.file +
                "')\
        }\
        ",
            ),
          );
        });
        document.body.appendChild(newStyle);
      },
      error: (err) => {
        // Ошибка загрузки шрифтов: фоновая, логируем и продолжаем без кастомных шрифтов.
        console.warn('AppComponent: ошибка загрузки шрифтов', err);
      },
    });
  }

  panda = HEROES[0];

  sloth = HEROES[1];

  $auth: Subscription;

  ngOnInit() {
    this.initLang();

    updatePlace(this.dialogs);
    this._modalsContainerService.container = this.dialogs;
    this.authService.checkAuth();
    this._thirdPartyScriptsService.init();
    this.showCookieConsentBanner = !this._cookieConsentService.hasDecision;
    this._cookieConsentSubscription = this._cookieConsentService.status$.subscribe((status) => {
      this.showCookieConsentBanner = status === null;
      this._cdr.markForCheck();
    });

    const urlParams = new URLSearchParams(window.location.search);
    const doc = urlParams.get('doc') as DocDialogType | null;

    if (doc === 'personal-data' || doc === 'offer') {
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          first(),
        )
        .subscribe(() => {
          this._docDialogService.open(doc);

          this._router.navigate([], {
            queryParams: { doc: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        });
    } else if (doc !== null) {
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          first(),
        )
        .subscribe(() => {
          this._router.navigate([], {
            queryParams: { doc: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        });
    }
  }

  ngOnDestroy() {
    this.$auth.unsubscribe();
    this._notificationAboutSyllablesAddedSubscription?.unsubscribe();
    this._cookieConsentSubscription?.unsubscribe();
  }

  acceptCookieConsent(): void {
    this._cookieConsentService.acceptAll();
  }

  keepNecessaryCookies(): void {
    this._cookieConsentService.useNecessaryOnly();
  }

  openCookieDetails(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this._docDialogService.open('personal-data');
  }

  setLanguage(newLang: string): void {
    this.translate.use(newLang);
  }

  get isLogged() {
    return this.authService.isLogged;
  }

  toggleFullscreen() {
    if (this.isFullscreen) this.closeFullscreen();
    else this.openFullscreen(this.content.nativeElement);
    this.isFullscreen = !this.isFullscreen;
  }

  openFullscreen(elem) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }

  closeFullscreen() {
    if (this.document.exitFullscreen) {
      this.document.exitFullscreen();
    } else if (this.document.mozCancelFullScreen) {
      /* Firefox */
      this.document.mozCancelFullScreen();
    } else if (this.document.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      this.document.webkitExitFullscreen();
    } else if (this.document.msExitFullscreen) {
      /* IE/Edge */
      this.document.msExitFullscreen();
    }
  }

  title = 'testing-sw';

  private initLang() {
    type ApiLanguage = {
      code: string;
      title: string;
      nativeTitle: string;
      enTitle: string;
      translationReady: boolean;
    };

    this.apiService
      .get<ApiLanguage[]>('2/languages/')
      .pipe(map((langs) => langs.filter((lang) => lang.translationReady).map((lang) => lang.code)))
      .subscribe((languages) => {
        this.translate.addLangs(languages);
        const browserLanguageCandidates = this._getBrowserLanguageCandidates();

        const localStorageLang = localStorage.getItem('language');
        const initialLang = this._resolveSupportedLanguage(
          [localStorageLang, ...browserLanguageCandidates],
          languages,
        );

        // Дожидаемся загрузки словаря перед активацией fallback
        this.translate.use(initialLang).subscribe({
          next: () => {
            // Язык фолбэка намеренно не зависит от языка браузера: ngx-translate применяет
            // defaultLang только при defaultLang !== currentLang, поэтому фолбэк, выведенный
            // из браузера, отключался бы ровно там, где браузер совпал с языком интерфейса,
            // и вместо перевода в UI попадал бы сырой ключ.

            // Устанавливаем начальный язык в HTML
            if (this.document?.documentElement) {
              this.document.documentElement.lang = initialLang;
            }
            this._appLoaderService.markTranslationsReady();

            // Дождаться загрузки fallback-цепочки
            this._translationFallbackService.activate(languages).subscribe({
              next: () => {
                this._appLoaderService.markFallbackReady();
                this._notifyIfTranslationsLoadFailed(initialLang, languages);
              },
              error: () => {
                this._appLoaderService.markFallbackReady();
                this._notifyIfTranslationsLoadFailed(initialLang, languages);
              },
            });
          },
          error: () => {
            this._appLoaderService.markTranslationsReady();
            this._appLoaderService.markFallbackReady();
            // Детект провала и в error-ветке: словарь не загрузился, уведомление должно показаться
            this._notifyIfTranslationsLoadFailed(initialLang, languages);
          },
        });

        this.loadedUser = this.userService.loadedUser.pipe(first((val) => !!val));

        this.loadedUser.subscribe((user) => {
          const userLang = this._resolveSupportedLanguage(
            [user?.user?.language, localStorageLang, ...browserLanguageCandidates],
            languages,
          );
          this.translate.use(userLang).subscribe(() => {
            // Обновляем язык в HTML при загрузке пользователя
            if (this.document?.documentElement) {
              this.document.documentElement.lang = userLang;
            }
          });
        });
      });

    this.translate.onLangChange.pipe(skip(1)).subscribe((val) => {
      const lang = val.lang;
      this._dismissTranslationFailureNotification();
      localStorage.setItem('language', lang);
      this.userService.saveLang(lang).subscribe({
        error: (err) => {
          // Ошибка сохранения языка: фоновая, логируем; выбранный язык уже применён локально.
          console.warn('AppComponent: ошибка сохранения языка', err);
        },
      });
      // Обновляем атрибут lang в HTML, чтобы браузер не предлагал перевод
      if (this.document?.documentElement) {
        this.document.documentElement.lang = lang;
      }
      console.debug(lang, this.menarService.isMenarVoiceLang(lang));
      if (this.menarService.isMenarVoiceLang(lang)) {
        this.menarService
          .getSettings()
          .pipe(
            switchMap((settings) =>
              this.menarService.saveSettings({
                ...settings,
                voiceLang: lang,
              }),
            ),
          )
          .subscribe({
            error: (err) => {
              // Ошибка синхронизации voiceLang в Menar: фоновая, логируем.
              console.warn('AppComponent: ошибка сохранения voiceLang', err);
            },
          });
      }
    });
  }

  private _getBrowserLanguageCandidates(): string[] {
    const browserNavigator = this.document?.defaultView?.navigator;
    const browserLanguages =
      Array.isArray(browserNavigator?.languages) && browserNavigator.languages.length
        ? browserNavigator.languages
        : [browserNavigator?.language, this.translate.getBrowserLang()].filter(
            (lang): lang is string => !!lang,
          );

    return Array.from(
      new Set(browserLanguages.flatMap((lang) => this._buildLanguageVariants(lang))),
    );
  }

  private _resolveSupportedLanguage(
    candidates: Array<string | null | undefined>,
    supportedLanguages: string[],
  ): string {
    const supportedLanguagesMap = new Map(
      supportedLanguages.map((lang) => [lang.toLowerCase(), lang] as const),
    );

    for (const candidate of candidates) {
      const variants = this._buildLanguageVariants(candidate);
      for (const variant of variants) {
        const matchedLanguage = supportedLanguagesMap.get(variant);
        if (matchedLanguage) {
          return matchedLanguage;
        }
      }
    }

    return supportedLanguagesMap.get('en') ?? 'en';
  }

  private _buildLanguageVariants(language: string | null | undefined): string[] {
    if (!language) {
      return [];
    }

    const normalizedLanguage = language.toLowerCase().replace(/_/g, '-');
    const [baseLanguage, ...regionParts] = normalizedLanguage.split('-');
    const mappedBaseLanguage = baseLanguage === 'uk' ? 'ua' : baseLanguage;
    const normalizedWithAlias = regionParts.length
      ? `${mappedBaseLanguage}-${regionParts.join('-')}`
      : mappedBaseLanguage;

    return Array.from(
      new Set([normalizedLanguage, baseLanguage, normalizedWithAlias, mappedBaseLanguage]),
    );
  }

  /**
   * Показать постоянное уведомление, если загрузка переводов финально провалилась.
   *
   * Финальный провал — одновременно:
   * 1) словарь начального языка пуст (нет ни одного ключа);
   * 2) ни один словарь fallback-цепочки не загружен.
   * Если основной словарь пуст, но fallback поднялся — UI работает на фолбэке, уведомление не нужно.
   *
   * Вызывается после завершения `translate.use(initialLang)` + `activate(languages)`.
   * Показ однократный: повторный детект игнорируется (флаг `_translationFailureNotificationShown`).
   */
  private _notifyIfTranslationsLoadFailed(initialLang: string, availableLangs: string[]): void {
    if (this._translationFailureNotificationShown) {
      return;
    }

    const mainTranslations = this.translate.translations[initialLang];
    const isMainEmpty = !mainTranslations || Object.keys(mainTranslations).length === 0;

    const hasFallbackLoaded = availableLangs.some((lang) => {
      if (lang === initialLang) {
        return false;
      }
      const translations = this.translate.translations[lang];
      return !!translations && Object.keys(translations).length > 0;
    });

    if (!isMainEmpty || hasFallbackLoaded) {
      return;
    }

    this._translationFailureNotificationShown = true;

    const navigatorLang = this.document?.defaultView?.navigator?.language;
    const message = resolveTranslationFailureMessage(initialLang, navigatorLang);
    // Язык не определён — показываем обе строки (RU сверху, EN снизу)
    const text = message ?? `${TRANSLATION_FAILURE_MESSAGES.ru}\n${TRANSLATION_FAILURE_MESSAGES.en}`;

    // Осознанный обход ToastrGuardService: тот подавляет тосты без загруженных переводов,
    // а уведомление о провале загрузки переводов должно быть видно всегда.
    ToastrService.prototype.warning.call(this._toastrService, text, undefined, {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: false,
      tapToDismiss: false,
      enableHtml: false,
    });
  }

  /**
   * Скрыть постоянное уведомление о провале загрузки переводов.
   *
   * Срабатывает при реальной смене языка после старта (onLangChange со skip(1)):
   * ретраи добились загрузки или пользователь сам сменил язык — дальше UI уже на переводах.
   */
  private _dismissTranslationFailureNotification(): void {
    if (!this._translationFailureNotificationShown) {
      return;
    }
    this._translationFailureNotificationShown = false;
    this._toastrService.clear();
  }

  /**
   * Настроить `MatIconRegistry`.
   *
   * Нужно для указания путей к наборам SVG-иконок, а также прописыванию классов для шрифтовых иконок,
   * чтобы использовать всё это в компоненте `mat-icon`.
   *
   * @see [Link](https://material.angular.io/components/icon/overview)
   */
  private _setupMatIconRegistry(): void {
    this._matIconRegistry.addSvgIconSet(
      this._sanitizer.bypassSecurityTrustResourceUrl('assets/icons/icon-set.svg'),
    );
    this._matIconRegistry.registerFontClassAlias('font-awesome', 'fas');
  }

  private _uninstallServiceWorkerAndClearCache(): void {
    if ('caches' in window) {
      caches.keys().then((keyList) => {
        return Promise.all(keyList.map((key) => caches.delete(key)));
      });
    }

    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      });
    }
  }

  /**
   * Настроить перехватчики для замены доменов в ссылках
   */
  private _setupDomainReplacer(): void {
    // 1. Перехват обычных кликов по ссылкам
    this.document.addEventListener(
      'click',
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const link = target.closest('a[href]') as HTMLAnchorElement;

        if (link && link.href) {
          const replacedUrl = this.domainReplacer.replaceDomain(link.href);
          if (replacedUrl !== link.href) {
            event.preventDefault();
            event.stopPropagation();

            // Сохраняем оригинальный target ссылки
            const targetAttr = link.target || link.getAttribute('target');
            if (targetAttr === '_blank' || event.ctrlKey || event.metaKey) {
              window.open(replacedUrl, '_blank');
            } else {
              window.location.href = replacedUrl;
            }
            return false;
          }
        }
      },
      true,
    );

    // 2. Перехват Router навигации (если URL содержит внешний домен)
    this._router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const url = event.url;
        // Проверяем, является ли это абсолютным URL с доменом проекта
        if (url.startsWith('http://') || url.startsWith('https://')) {
          const replaced = this.domainReplacer.replaceDomain(url);
          if (replaced !== url) {
            // Отменяем текущую навигацию и перенаправляем на замененный URL
            this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              window.location.href = replaced;
            });
          }
        }
      });

    // 3. Перехват window.open() для замены доменов
    const originalWindowOpen = window.open;
    window.open = (url?: string | URL, target?: string, features?: string): Window | null => {
      if (url && typeof url === 'string') {
        const replacedUrl = this.domainReplacer.replaceDomain(url);
        return originalWindowOpen.call(window, replacedUrl, target, features);
      }
      return originalWindowOpen.call(window, url, target, features);
    };
  }
}
